/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_rec_arr, t_key } from '../types';
import fold from './fold';

type t_find_fn<V> = (val: V, key: t_key) => boolean;

export interface Find {
  /**
   * Find a single value in an array
   *
   * @public
   * @function f1.find
   * @param adt the array or record we will find
   * @example
   *
   * ```ts
   * f1.find(v => v > 16, [16, 17, 18]) === 17
   * ```
   *
   */
  <V>(query: t_find_fn<V>, adt: t_rec_arr<V>): V | undefined;
  <V>(query: t_find_fn<V>): (adt: t_rec_arr<V>) => V | undefined;
}

/**
 * @see Find
 */

export default <V>(query: t_find_fn<V>, adt: t_rec_arr<V>): V | undefined => {
  return fold<V, V | undefined, V | undefined>(
    (found, v, k, adt, stop) => {
      if (query(v, k)) {
        stop();
        return v;
      }

      return found;
    },
    undefined,
    adt
  );
};
